<script setup lang="ts">
  defineOptions({
    name: 'CPDrawer',
  })

  // External library imports
  import type { Component, ComponentPublicInstance, PropType } from 'vue'

  import { computed, nextTick, onUnmounted, ref, watch } from 'vue'

  import { HLDrawer, HLDrawerContent } from '@platform-ui/highrise'

  import { ChevronRightIcon } from '@gohighlevel/ghl-icons/24/outline'

  // Component imports
  import CPButton from './CPButton.vue'

  import CPText from './CPText.vue'
  import type { CSSProperties, DrawerAction, DrawerOption, DrawerWindow, PlacementType, ScrollbarProps } from './types/drawer.types'

  // Props definitions
  const props = defineProps({
    /**
     * Unique identifier for the drawer
     */
    id: {
      type: String,
      required: true,
    },
    /**
     * Whether to show the drawer
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * Width of the drawer (works when placement is left/right)
     */
    width: {
      type: [Number, String] as PropType<number | string>,
      default: 502,
    },
    /**
     * Height of the drawer (works when placement is top/bottom)
     */
    height: {
      type: [Number, String] as PropType<number | string>,
      default: undefined,
    },
    /**
     * Drawer placement
     */
    placement: {
      type: String as PropType<PlacementType>,
      default: 'right',
    },
    /**
     * Whether to resize the drawer
     */
    resizable: {
      type: Boolean,
      default: false,
    },
    /**
     * Min width of draggable drawer
     */
    minWidth: {
      type: Number,
      default: undefined,
    },
    /**
     * Max width of draggable drawer
     */
    maxWidth: {
      type: Number,
      default: undefined,
    },
    /**
     * Min height of draggable drawer
     */
    minHeight: {
      type: Number,
      default: undefined,
    },
    /**
     * Max height of draggable drawer
     */
    maxHeight: {
      type: Number,
      default: undefined,
    },
    /**
     * Whether to close drawer when clicking the mask
     */
    maskClosable: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to show mask
     */
    showMask: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to trap focus inside drawer
     */
    trapFocus: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to auto focus the first focusable element
     */
    autoFocus: {
      type: Boolean,
      default: true,
    },
    /**
     * Container node of the drawer
     */
    to: {
      type: [String, Object] as PropType<string | HTMLElement>,
      default: 'body',
    },
    /**
     * Z-index of the drawer
     */
    zIndex: {
      type: Number,
      default: undefined,
    },
    /**
     * Class of drawer's scrollable content node
     */
    contentClass: {
      type: String,
      default: undefined,
    },
    /**
     * Style of drawer's scrollable content node
     */
    contentStyle: {
      type: [String, Object] as PropType<string | CSSProperties>,
      default: undefined,
    },
    /**
     * Scrollbar props
     */
    scrollbarProps: {
      type: Object as PropType<ScrollbarProps>,
      default: undefined,
    },
    /**
     * Display directive ('if' or 'show')
     */
    displayDirective: {
      type: String as PropType<'if' | 'show'>,
      default: 'if',
    },
    // DrawerContent props
    /**
     * Drawer content's header title
     */
    title: {
      type: String,
      default: undefined,
    },
    /**
     * Drawer content's header description
     */
    description: {
      type: String,
      default: undefined,
    },
    /**
     * Drawer content's header icon
     */
    icon: {
      type: Object as PropType<Component>,
      default: undefined,
    },
    /**
     * Whether to show the header
     */
    showHeader: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether to show the close button in header
     */
    closable: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to use native scrollbar on body
     */
    nativeScrollbar: {
      type: Boolean,
      default: true,
    },
    /**
     * Drawer content's header class
     */
    headerClass: {
      type: String,
      default: undefined,
    },
    /**
     * Drawer content's header style
     */
    headerStyle: {
      type: [String, Object] as PropType<string | CSSProperties>,
      default: undefined,
    },
    /**
     * Drawer content's body class
     */
    bodyClass: {
      type: String,
      default: undefined,
    },
    /**
     * Drawer content's body style
     */
    bodyStyle: {
      type: [String, Object] as PropType<string | CSSProperties>,
      default: undefined,
    },
    /**
     * Class of body's scrollable content node
     */
    bodyContentClass: {
      type: String,
      default: undefined,
    },
    /**
     * Style of body's scrollable content node
     */
    bodyContentStyle: {
      type: [String, Object] as PropType<string | CSSProperties>,
      default: undefined,
    },
    /**
     * Drawer content's footer class
     */
    footerClass: {
      type: String,
      default: undefined,
    },
    /**
     * Drawer content's footer style
     */
    footerStyle: {
      type: [String, Object] as PropType<string | CSSProperties>,
      default: undefined,
    },
    // Options and Actions props
    /**
     * Array of options to display as a list
     */
    options: {
      type: Array as PropType<DrawerOption[]>,
      default: () => [],
    },
    /**
     * Array of action buttons to display at the bottom
     */
    actions: {
      type: Array as PropType<DrawerAction[]>,
      default: () => [],
    },
    /**
     * Whether to use custom content mode (uses default slot instead of options)
     */
    customContent: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether to close drawer when clicking outside
     */
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    'update:show': [value: boolean]
    afterEnter: []
    afterLeave: []
    maskClick: []
    esc: []
    'update:width': [width: number]
    'update:height': [height: number]
    optionSelected: [key: string]
    actionClicked: [key: string]
    close: []
  }>()

  // State
  const showDrawer = ref(false)

  const drawerContentRef = ref<HTMLElement | null>(null)

  const clickOutsideTimeoutId = ref<number | null>(null)

  // Computed properties
  const drawerId = computed(() => `${props.id}-drawer`)

  const drawerContentId = computed(() => `${props.id}-drawer-content`)

  const drawerBodyDefaultStyle = {} as const

  const mergedBodyStyle = computed(() => {
    if (typeof props.bodyStyle === 'string') {
      return `padding-bottom: max(16px, env(safe-area-inset-bottom)); ${props.bodyStyle}`
    }

    return {
      paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
      ...(props.bodyStyle || {}),
    }
  })

  // Helper functions
  const getDrawerContentElement = (): HTMLElement | null => {
    const el = drawerContentRef.value
    if (!el) return null
    if (el instanceof HTMLElement) return el
    const cmp = el as ComponentPublicInstance
    const root = cmp.$el
    return root instanceof HTMLElement ? root : null
  }

  const getDrawerStack = (): string[] => {
    const drawerWindow = window as DrawerWindow
    if (!drawerWindow.__cpDrawerStack) {
      drawerWindow.__cpDrawerStack = []
    }
    return drawerWindow.__cpDrawerStack
  }

  // User-defined functions
  const handleClickOutside = (event: MouseEvent) => {
    if (!showDrawer.value || !props.closeOnClickOutside) return

    // Only the top-most open drawer should react to outside clicks
    if (process.client) {
      const stack = getDrawerStack()
      if (stack.length && stack[stack.length - 1] !== drawerId.value) {
        return
      }
    }

    const target = event.target as HTMLElement
    // If the click originated inside another drawer instance (child/sibling),
    // ignore it so we don't close this drawer.
    const nearestDrawer = target.closest('[id$="-drawer"]') as HTMLElement | null
    if (nearestDrawer && nearestDrawer.id !== drawerId.value) {
      return
    }

    const drawerContentElement = getDrawerContentElement()

    if (drawerContentElement && drawerContentElement.contains(target)) {
      return
    }

    const drawerElement = target.closest(`[id="${drawerId.value}"]`)

    if (drawerElement && target !== drawerElement && drawerContentElement && !drawerContentElement.contains(target)) {
      closeDrawer()
      return
    }

    if (!drawerElement) {
      closeDrawer()
    }
  }

  const closeDrawer = () => {
    showDrawer.value = false
  }

  const handleOptionClick = (option: DrawerOption) => {
    if (option.disabled) return
    emit('optionSelected', option.key)
  }

  const handleActionClick = (action: DrawerAction) => {
    if (action.disabled || action.loading) return
    emit('actionClicked', action.key)
  }

  const handleAfterEnter = () => {
    emit('afterEnter')
  }

  const handleAfterLeave = () => {
    emit('afterLeave')
  }

  const handleMaskClick = () => {
    emit('maskClick')
  }

  const handleEsc = () => {
    emit('esc')
  }

  const handleUpdateWidth = (width: number) => {
    emit('update:width', width)
  }

  const handleUpdateHeight = (height: number) => {
    emit('update:height', height)
  }

  const handleDrawerContentClose = (show: boolean) => {
    showDrawer.value = show
  }

  // Lifecycle hooks
  onUnmounted(() => {
    if (process.client) {
      // Clear any pending timeout to prevent orphaned event listener
      if (clickOutsideTimeoutId.value !== null) {
        window.clearTimeout(clickOutsideTimeoutId.value)
        clickOutsideTimeoutId.value = null
      }
      document.removeEventListener('click', handleClickOutside, true)
      // Clean up from stack on unmount
      const stack = getDrawerStack()
      const idx = stack.indexOf(drawerId.value)
      if (idx !== -1) stack.splice(idx, 1)
    }
  })

  // Watchers
  watch(
    () => showDrawer.value,
    (newValue: boolean) => {
      if (process.client && props.closeOnClickOutside) {
        if (newValue) {
          // Clear any pending timeout before creating a new one
          if (clickOutsideTimeoutId.value !== null) {
            window.clearTimeout(clickOutsideTimeoutId.value)
            clickOutsideTimeoutId.value = null
          }
          nextTick(() => {
            clickOutsideTimeoutId.value = window.setTimeout(() => {
              clickOutsideTimeoutId.value = null
              document.addEventListener('click', handleClickOutside, true)
            }, 0)
          })
        } else {
          // Clear pending timeout if drawer is closed before timeout executes
          if (clickOutsideTimeoutId.value !== null) {
            window.clearTimeout(clickOutsideTimeoutId.value)
            clickOutsideTimeoutId.value = null
          }
          document.removeEventListener('click', handleClickOutside, true)
        }
      }

      // Maintain a global stack of open drawers (client-only)
      if (process.client) {
        const stack = getDrawerStack()
        const idVal = drawerId.value
        if (newValue) {
          // Push to top if not already present
          const idx = stack.indexOf(idVal)
          if (idx !== -1) stack.splice(idx, 1)
          stack.push(idVal)
        } else {
          const idx = stack.indexOf(idVal)
          if (idx !== -1) stack.splice(idx, 1)
        }
      }
    }
  )

  watch(
    () => props.show,
    (newValue: boolean) => {
      showDrawer.value = newValue
    },
    { immediate: true }
  )

  watch(
    () => showDrawer.value,
    (newValue: boolean) => {
      emit('update:show', newValue)
      if (!newValue) {
        emit('close')
      }
    }
  )
</script>

<template>
  <HLDrawer
    :id="drawerId"
    :show="showDrawer"
    :width="width"
    :height="height"
    :placement="placement"
    :resizable="resizable"
    :min-width="minWidth"
    :max-width="maxWidth"
    :min-height="minHeight"
    :max-height="maxHeight"
    :mask-closable="maskClosable"
    :show-mask="showMask"
    :trap-focus="trapFocus"
    :auto-focus="autoFocus"
    :to="to"
    :z-index="zIndex"
    :content-class="contentClass"
    :content-style="contentStyle"
    :scrollbar-props="scrollbarProps"
    :display-directive="displayDirective"
    @update:show="showDrawer = $event"
    @after-enter="handleAfterEnter"
    @after-leave="handleAfterLeave"
    @mask-click="handleMaskClick"
    @esc="handleEsc"
    @update:width="handleUpdateWidth"
    @update:height="handleUpdateHeight"
  >
    <HLDrawerContent
      :id="drawerContentId"
      ref="drawerContentRef"
      :title="showHeader && !options.length && placement !== 'bottom' ? title : undefined"
      :description="showHeader && !options.length && placement !== 'bottom' ? description : undefined"
      :icon="showHeader && !options.length && placement !== 'bottom' ? icon : undefined"
      :show-header="showHeader && !options.length && placement !== 'bottom'"
      :closable="closable && !options.length && placement !== 'bottom'"
      :native-scrollbar="nativeScrollbar"
      :header-class="headerClass"
      :header-style="headerStyle"
      :body-class="options.length ? 'p-0' : bodyClass"
      :body-style="mergedBodyStyle"
      :body-content-class="bodyContentClass"
      :body-content-style="bodyContentStyle"
      :footer-class="footerClass"
      :footer-style="footerStyle"
      :scrollbar-props="scrollbarProps"
      @update:show="handleDrawerContentClose"
    >
      <!-- Custom header slot (for custom layouts) -->
      <template v-if="$slots.header" #header>
        <slot name="header" />
      </template>

      <!-- Bottom placement: drag handle + sheet header -->
      <template v-if="placement === 'bottom' && !$slots.header">
        <div class="flex items-center justify-center py-2 bg-background">
          <div class="h-1 w-10 rounded-full bg-hover" />
        </div>
        <div v-if="title || description" class="px-4 pb-3 bg-background text-left">
          <CPText v-if="title" size="lg" weight="semibold" class="text-body">
            {{ title }}
          </CPText>
          <CPText v-if="description" size="sm" class="text-secondary mt-1">
            {{ description }}
          </CPText>
        </div>
      </template>

      <!-- Options/Actions Mode -->
      <template v-if="options.length > 0 || actions.length > 0">
        <!-- Header for options mode -->
        <div v-if="title || description" class="px-4 pt-4 pb-2 border-b border-default">
          <CPText v-if="title" size="lg" weight="semibold" class="mb-1">
            {{ title }}
          </CPText>
          <CPText v-if="description" size="sm" class="text-secondary">
            {{ description }}
          </CPText>
        </div>

        <!-- Options List -->
        <div v-if="options.length > 0" class="divide-y divide-default">
          <div
            v-for="option in options"
            :id="option.id || `${id}-option-${option.key}`"
            :key="option.key"
            :class="[
              'flex items-center justify-between px-4 py-4 cursor-pointer transition-colors',
              option.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-hover active:bg-hover',
              option.danger ? 'text-danger' : '',
            ]"
            :aria-disabled="option.disabled"
            tabindex="0"
            role="button"
            @click="handleOptionClick(option)"
            @keydown.enter.prevent="handleOptionClick(option)"
            @keydown.space.prevent="handleOptionClick(option)"
          >
            <!-- Left: Icon and Label -->
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <component
                :is="option.icon"
                v-if="option.icon"
                :class="['w-5 h-5 flex-shrink-0', option.danger ? 'text-danger' : 'text-secondary']"
              />
              <div class="flex-1 min-w-0">
                <CPText size="base" :class="option.danger ? 'text-danger' : 'text-body'">
                  {{ option.label }}
                </CPText>
                <CPText v-if="option.description" size="sm" class="text-muted mt-0.5">
                  {{ option.description }}
                </CPText>
              </div>
            </div>

            <!-- Right: Arrow Icon -->
            <ChevronRightIcon v-if="!option.disabled" :class="['w-5 h-5 flex-shrink-0', option.danger ? 'text-danger' : 'text-muted']" />
          </div>
        </div>

        <!-- Custom content when in options mode but no options provided -->
        <div v-if="!options.length && customContent" class="p-4">
          <slot />
        </div>

        <!-- Actions (buttons at bottom) -->
        <div v-if="actions.length > 0" class="px-4 py-4 border-t border-default space-y-2">
          <CPButton
            v-for="action in actions"
            :id="action.id || `${id}-action-${action.key}`"
            :key="action.key"
            :type="action.type || 'secondary'"
            :color="action.color || 'primary'"
            :disabled="action.disabled"
            :loading="action.loading"
            class="w-full"
            @click="handleActionClick(action)"
          >
            {{ action.label }}
          </CPButton>
        </div>
      </template>

      <!-- Default content slot (for standard drawer usage) -->
      <template v-else>
        <slot />
      </template>

      <!-- Custom footer slot (for standard drawer usage) -->
      <template v-if="$slots.footer && !actions.length" #footer>
        <slot name="footer" />
      </template>
    </HLDrawerContent>
  </HLDrawer>
</template>

<style>
  .hr-drawer.hr-drawer--bottom-placement {
    border-top-left-radius: 20px !important;
    border-top-right-radius: 20px !important;
  }

  .hr-drawer,
  .hr-drawer-body,
  .hr-drawer-body-content-wrapper,
  .hr-drawer-body-content,
  .hr-drawer-content,
  .hr-drawer-header {
    background-color: var(--cp-background-500) !important;
    color: var(--cp-brand-font) !important;
  }
</style>
